import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import {
  API_CREATE_DEPARTMENT,
  API_UPDATE_DEPARTMENT,
  GET_DEPARTMENT_LIST,
} from "../../../../../config/Endpoints.js";
import {
  ApiError,
  GENERIC_ERROR,
  getApi,
  postApi,
  putApi,
} from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import type { DepartmentRow, MutationResult } from "../types.ts";
import { extractApiMessage } from "../utils/extract-api-message.ts";
import { unwrapApiDataArray } from "../utils/unwrap-api-data.ts";

const departmentRowSchema = z.object({
  id: z.union([z.number(), z.string()]).transform((v) => Number(v)),
  name: z.string(),
  creationDate: z.string().nullish(),
  status: z.string().nullish(),
});

function parseDepartments(raw: unknown): DepartmentRow[] {
  const items = unwrapApiDataArray<unknown>(raw);
  const parsed = z.array(departmentRowSchema).safeParse(items);
  if (!parsed.success) return [];
  return parsed.data;
}

export function useDepartments() {
  const [departments, setDepartments] = useState<DepartmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDepartments = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getApi<unknown>(
        GET_DEPARTMENT_LIST,
        accessToken(),
        "json",
        signal,
      );
      setDepartments(parseDepartments(data));
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : GENERIC_ERROR;
      setError(msg);
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    void fetchDepartments(ac.signal);
    return () => ac.abort();
  }, [fetchDepartments]);

  const createDepartment = useCallback(
    async (name: string): Promise<MutationResult> => {
      setSubmitting(true);
      try {
        const { data } = await putApi(API_CREATE_DEPARTMENT, accessToken(), {
          name: name.trim(),
        });
        await fetchDepartments();
        return { ok: true, message: extractApiMessage(data) };
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : GENERIC_ERROR;
        return { ok: false, message: msg };
      } finally {
        setSubmitting(false);
      }
    },
    [fetchDepartments],
  );

  const updateDepartment = useCallback(
    async (id: number, name: string): Promise<MutationResult> => {
      setSubmitting(true);
      try {
        const { data } = await postApi(API_UPDATE_DEPARTMENT, accessToken(), {
          id,
          name: name.trim(),
        });
        await fetchDepartments();
        return { ok: true, message: extractApiMessage(data) };
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : GENERIC_ERROR;
        return { ok: false, message: msg };
      } finally {
        setSubmitting(false);
      }
    },
    [fetchDepartments],
  );

  return {
    departments,
    loading,
    submitting,
    error,
    refetch: fetchDepartments,
    createDepartment,
    updateDepartment,
  };
}
